import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useU2000LocalStorage from "./U2000UseLocalStorage";
import {
  U2000_TIMESHEET_WEEK_DAYS,
  u2000TimesheetWeekDayIsoDates,
  u2000TimesheetVisibleWeekDays,
  type U2000TimesheetWeekDay,
} from "../tools/U2000TimesheetHonorariumDayTotals";

export type U2000TimesheetWeekView = {
  showWeekend: boolean;
  setShowWeekend: (value: boolean) => void;
  visibleDays: readonly U2000TimesheetWeekDay[];
  dayLabelsWithDates: Record<string, string> | null;
  dayDates: Record<U2000TimesheetWeekDay, string> | null;
};

/**
 * Case week-end + libellés jour/période partagés entre onglets Honoraires et Absences/banques (U3129 / U3130).
 */
export function useU2000TimesheetWeekView(periodDate: string | undefined | null, i18nNs: string, weekendStorageKey: string): U2000TimesheetWeekView {
  const { t } = useTranslation();
  const [showWeekend, setShowWeekend] = useU2000LocalStorage<boolean>(weekendStorageKey, true);

  const visibleDays = useMemo(() => u2000TimesheetVisibleWeekDays(showWeekend), [showWeekend]);

  const dayDates = useMemo(() => u2000TimesheetWeekDayIsoDates(periodDate), [periodDate]);

  const dayLabelsWithDates = useMemo(() => {
    if (!dayDates) return null;
    const labels: Record<string, string> = {};
    U2000_TIMESHEET_WEEK_DAYS.forEach((d) => {
      const iso = dayDates[d];
      const dayNum = Number(iso.slice(8, 10));
      labels[d] = `${t(`lbl${d.charAt(0).toUpperCase()}${d.slice(1)}`, { ns: i18nNs })} ${dayNum}`;
    });
    return labels;
  }, [dayDates, i18nNs, t]);

  return { showWeekend, setShowWeekend, visibleDays, dayLabelsWithDates, dayDates };
}
